// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import xLocale from "@werner/XDL/Utilities/xValidate/xLocale";
import startCase from "lodash/startCase";
// eslint-disable-next-line spire/fenced-imports
import * as Yup from "yup";

Yup.setLocale(xLocale);

type expandType = { [key: string]: any[] | any };

const putAtFirst = (toBeFirst: string, schema: string) => {
    const rules = schema.split("|");
    const indexOfType = rules.indexOf(toBeFirst);
    if (indexOfType !== 0) {
        delete rules[indexOfType];
        rules.unshift(toBeFirst);
        schema = rules.filter(Boolean).join("|");
    }
    return schema;
};

const expand = (schemas: string, key = "", required = false): expandType => {
    // available yup types
    const types = ["mixed", "string", "number", "boolean", "date", "array", "object"];
    // check if the schema has a type
    const type = types.find(type => schemas.includes(type));

    // put required if needed
    const hasRequired = schemas.includes("required");
    if (hasRequired) {
        schemas = putAtFirst("required", schemas);
    }
    if (required && !hasRequired) {
        schemas = `required|${schemas}`;
    }

    // default schema type to string
    if (!type) {
        schemas = `string|${schemas}`;
    } else {
        // if type is not the first element then make it the first one
        schemas = putAtFirst(type, schemas);
    }
    // key passed, inject label
    if (key && !schemas.includes("label")) {
        const label = startCase(key);
        schemas = `${schemas}|label:${label}`;
    }
    /**
     *  generate an object be like {
     *          min: ['5'],
     *          max: ['256']
     * }
     *  */
    return schemas.split("|").reduce((result, schema) => {
        const ex = schema.split(":");
        result[ex[0]] = ex[1] ? ex[1].split(",") : [];
        return result;
    }, {} as expandType);
};

// re-build the validation rules from string|email|min:5 to yup.string().email.min('5')
export const buildValidator = (schemas: string, key?: string, required?: boolean) => {
    let validator: any = Yup;

    Object.entries(expand(schemas, key, required)).forEach(([method, params]) => {
        validator = validator || Yup;
        const canValidate = typeof validator[method] === "function";
        if (canValidate) {
            validator = validator[method](...params);
        } else {
            validator[method] = params;
        }
    });
    return validator;
};

// apply validation rules on a value
const validate = (value: any, schemas: string, key?: string, required = false) => {
    const validator = buildValidator(schemas, key, required);
    let errorMessage = "";
    if (!value && !required) {
        // empty string, no error needded
        return errorMessage;
    }

    try {
        validator.validateSync(value);
    } catch (error) {
        errorMessage = error.message;
    }

    return errorMessage;
};

export default validate;
